import React from 'react';
import {observer} from 'mobx-react-lite';
import {Grid, makeStyles, Tooltip} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {Prequal} from '../../Core/mobx/ParameterStore/Prequal';
import RootStore from '../../Core/mobx/RootStore';
import {Parameter} from '../../Core/mobx/ParameterStore/ParameterStore';
import {NumberFormatCustom, SwitchCustom, TextBox} from '../../Core/UI';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../Core/mobx/ParameterStore/PrequalXref';
import {Approval} from '../../Core/mobx/ParameterStore/Approval';

const useStyles = makeStyles({
    tooltip: {
        maxWidth: 500,
        width: '100%',
        background: '#fff',
        border: '1px solid gray',
        borderRadius: '10px',
        padding: '0 10px',
        margin: 0,
        color: '#444',
        fontFamily: 'FredokaRegular',
        fontSize: '18px',
        lineHeight: '24px',
        '& h1': {
            color: '#3f51b5',
            fontWeight: 600,
            fontSize: '26px',
            lineHeight: '26.px',
        },
    },
});

type ExistingPoliciesProps = {
    parameters: Prequal | Approval;
    valueChanged: (p?: Parameter & {name?: string}, value?: any) => void;
    preapproval?: boolean;
};

const ExistingPolicies = observer(({parameters, valueChanged, preapproval}: ExistingPoliciesProps) => {
    const classes = useStyles();
    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );
    return (
        <>
            <Grid item xs={preapproval ? 4 : 12}>
                <SwitchCustom
                    param={{
                        label: preapproval ? 'Existing Life Insurance Policies' : 'Existing Coverage',
                        ...parameters.existingCoverage,
                        name: 'existingCoverage',
                    }}
                    required={preapproval ? required('') : ''}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.existingCoverage.value && (
                <>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: preapproval ? 'Policy Type' : 'What type of policy do you have?',
                                options: fieldOptions.existingPolicyType.options,
                                ...parameters.existingPolicyType,
                                name: 'existingPolicyType',
                            }}
                            required={preapproval ? required('') : ''}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'existingCarrier'}
                            label={
                                preapproval
                                    ? required('Insurance Company Name')
                                    : 'Which carrier do you have the policy with?'
                            }
                            value={parameters.existingCarrier?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.existingCarrier, name: 'existingCarrier'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'existingCoverageAmount'}
                            label={
                                preapproval
                                    ? required('Coverage Amount')
                                    : 'What is the coverage amount of your existing policy?'
                            }
                            value={parameters.existingCoverageAmount?.value}
                            onChange={(value) =>
                                valueChanged(
                                    {...parameters.existingCoverageAmount, name: 'existingCoverageAmount'},
                                    value,
                                )
                            }
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                            }}
                        />
                    </Grid>
                    {preapproval && (
                        <>
                            <Grid item xs={4}>
                                <TextBox
                                    id={'policyNumber'}
                                    label={
                                        RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                            'FGPathsetterIUL',
                                        )
                                            ? required('Policy Number')
                                            : 'Policy Number'
                                    }
                                    value={parameters.policyNumber?.value}
                                    onChange={(value) =>
                                        valueChanged({...parameters.policyNumber, name: 'policyNumber'}, value)
                                    }
                                    InputProps={{required: true}}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextBox
                                    id={'accidentalDeathBenefitAmount'}
                                    label={
                                        RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                            'FGPathsetterIUL',
                                        )
                                            ? required('Accidental Death Benefit Amount')
                                            : 'Accidental Death Benefit Amount'
                                    }
                                    value={parameters.accidentalDeathBenefitAmount?.value}
                                    onChange={(value) =>
                                        valueChanged(
                                            {
                                                ...parameters.accidentalDeathBenefitAmount,
                                                name: 'accidentalDeathBenefitAmount',
                                            },
                                            value,
                                        )
                                    }
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextBox
                                    id={'howLongInforce'}
                                    label={
                                        RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                            'FGPathsetterIUL',
                                        )
                                            ? required('How Long Inforce?')
                                            : 'How Long Inforce?'
                                    }
                                    value={parameters.howLongInforce?.value}
                                    onChange={(value) =>
                                        valueChanged({...parameters.howLongInforce, name: 'howLongInforce'}, value)
                                    }
                                />
                            </Grid>
                        </>
                    )}
                    {preapproval && (
                        <Grid item xs={4}>
                            <div style={{float: 'left', maxWidth: 'calc(100% - 24px)'}}>
                                <SwitchCustom
                                    param={{
                                        label: 'Replacing Existing Policy?',
                                        ...parameters.replacingExistingPolicy,
                                        name: 'replacingExistingPolicy',
                                    }}
                                    required={required('')}
                                    valueChanged={valueChanged}
                                />
                            </div>
                            <Tooltip
                                arrow
                                leaveDelay={200}
                                placement="right"
                                classes={{tooltip: classes.tooltip}}
                                title={
                                    <>
                                        <h1>If Yes</h1>
                                        <p>
                                            Please clarify whether the applicant will complete a 1035 exchange in the
                                            Case Notes field.
                                        </p>
                                    </>
                                }
                            >
                                <InfoOutlinedIcon color="primary" />
                            </Tooltip>
                        </Grid>
                    )}
                </>
            )}
            {!preapproval && (
                <Grid item xs={12}>
                    <div style={{fontSize: 20, color: '#5D6CC1', fontWeight: 'bold', marginTop: '1em'}}>
                        Applied For Life Insurance
                    </div>
                </Grid>
            )}
            <Grid item xs={preapproval ? 4 : 12}>
                <SwitchCustom
                    param={{
                        label: preapproval ? 'Previous Applications' : 'Applied In Last Year',
                        ...parameters.shoppedCoverage,
                        name: 'shoppedCoverage',
                    }}
                    required={preapproval ? required('') : ''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <div style={{width: '100%'}} />
            {parameters.shoppedCoverage.value && (
                <>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: preapproval ? 'Policy Type' : 'What type of policy did you shop for?',
                                options: fieldOptions.shoppedPolicyType.options,
                                ...parameters.shoppedPolicyType,
                                name: 'shoppedPolicyType',
                            }}
                            required={preapproval ? required('') : ''}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'shoppedCarrier'}
                            label={preapproval ? required('Insurance Company Name') : 'Which carrier did you apply to?'}
                            value={parameters.shoppedCarrier?.value}
                            onChange={(value) =>
                                valueChanged({...parameters.shoppedCarrier, name: 'shoppedCarrier'}, value)
                            }
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'shoppedCoverageAmount'}
                            label={
                                preapproval
                                    ? required('Coverage Amount')
                                    : 'What is the coverage amount that you applied for?'
                            }
                            value={parameters.shoppedCoverageAmount?.value}
                            onChange={(value) =>
                                valueChanged(
                                    {...parameters.shoppedCoverageAmount, name: 'shoppedCoverageAmount'},
                                    value,
                                )
                            }
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                            }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <div style={{float: 'left', maxWidth: 'calc(100% - 24px)'}}>
                            <SwitchCustom
                                param={{
                                    label: 'Ever Been Declined',
                                    ...parameters.policyEverBeenDeclined,
                                    name: 'policyEverBeenDeclined',
                                }}
                                required={required('')}
                                valueChanged={valueChanged}
                            />
                        </div>
                        <Tooltip
                            arrow
                            leaveDelay={200}
                            placement="right"
                            classes={{tooltip: classes.tooltip}}
                            title={
                                <>
                                    <h1>If Yes</h1>
                                    <p>Please provide details in the case notes section.</p>
                                </>
                            }
                        >
                            <InfoOutlinedIcon color="primary" />
                        </Tooltip>
                    </Grid>
                </>
            )}
        </>
    );
});

export default ExistingPolicies;
